import { useCallback, useEffect, useRef, useState } from 'react';
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    LinearProgress,
    Paper,
    TextField,
    Typography,
} from '@mui/material';
import Close from '@mui/icons-material/Close';
import Edit from '@mui/icons-material/Edit';
import { DateTimePicker } from '@mui/x-date-pickers';
import type { Dayjs } from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { addEvent, deleteEvent, getAdminEvents, updateEvent } from '../../../api/events';
import { errorMessage } from '../../../api/errors';
import { eventImageUrl } from '../../../api/files';
import type { EventInfo } from '../../../api/types';
import { FileSelector } from '../../../components/FileSelector';
import { EmptyState } from '../../../components/ui/EmptyState';
import dayjs, { formatTime } from '../../../utils/format';
import { useAdminData } from '../../../hooks/useAdminData';
import { useNow } from '../../../hooks/useNow';
import { logout } from '../../../store/admin.slice';
import { logout as logoutRequest } from '../../../api/auth';
import { ADMIN_STORAGE_KEY } from '../../../api/client';

/**
 * Nome file della copertina: `event_image` può essere salvata come `/files/x.png`
 * o come URL assoluto, mentre `FileSelector` lavora con il solo nome del file.
 * Tutto ciò che precede l'ultimo `/` viene quindi scartato.
 */
function imageNameOf(image?: string | null): string {
    return (image ?? '').replace(/^.*\//, '');
}

/**
 * `img_path` inviato al backend: `/files/<nome>` (stessa forma usata dalla
 * creazione) oppure stringa vuota. Una stringa vuota **presente** significa
 * "rimuovi la copertina": è diversa dalla chiave assente, che la lascia
 * invariata. La creazione invia sempre il campo; la modifica solo se la
 * copertina è stata toccata (vedi `imgTouched`).
 */
function imagePayloadOf(name: string): string {
    return name ? `/files/${name}` : '';
}

export function EventSection() {
    const admin = useAdminData();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [events, setEvents] = useState<EventInfo[]>([]);
    const [loading, setLoading] = useState(true);
    const [reload, setReload] = useState(false);
    /** Messaggio d'errore/successo delle azioni (eliminazione, creazione, modifica). */
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    /** Evento in attesa di conferma di eliminazione (null = nessun dialog aperto). */
    const [pendingDelete, setPendingDelete] = useState<EventInfo | null>(null);
    /** Evento in modifica (null = il form crea un nuovo evento). */
    const [editing, setEditing] = useState<EventInfo | null>(null);
    /** Ancora al form, per portarci sopra la vista quando si avvia una modifica. */
    const formRef = useRef<HTMLDivElement>(null);

    // Il tempo trascorso è reattivo: il pulsante "Modifica" degli eventi iniziati
    // deve sparire senza ricaricare la pagina (stessa logica di EventStatusChip).
    const now = useNow();

    const [title, setTitle] = useState('');
    const [location, setLocation] = useState('');
    const [data, setData] = useState<Dayjs | null>(null);
    const [imgPath, setImgPath] = useState('');
    /**
     * `true` se l'admin ha toccato il selettore della copertina. In modifica la
     * chiave `img_path` resta assente se non è cambiata (P4): così una copertina
     * con URL esterno o `/assets/…` non viene sovrascritta con `/files/<nome>`.
     */
    const [imgTouched, setImgTouched] = useState(false);

    const handleUnauthorized = useCallback(() => {
        localStorage.removeItem(ADMIN_STORAGE_KEY);
        dispatch(logout());
        // M4: azzera anche il cookie di autenticazione lato server.
        void logoutRequest().catch(() => undefined);
        navigate('/admin/login');
    }, [dispatch, navigate]);

    useEffect(() => {
        if (!admin.logged) return;
        let active = true;
        setLoading(true);
        getAdminEvents()
            .then((list) => {
                if (active) setEvents(list);
            })
            .catch((err: unknown) => {
                if (!active) return;
                const status = (err as { response?: { status?: number } }).response?.status;
                if (status === 401) {
                    handleUnauthorized();
                    return;
                }
                // L'API risponde 404 quando non ci sono righe (stessa convenzione di
                // `/request/list` e `/files`): è una lista vuota, non un errore. Senza
                // questo ramo la lista precedente restava a schermo, così l'evento
                // appena eliminato continuava a comparire.
                setEvents([]);
            })
            .finally(() => {
                if (active) setLoading(false);
            });
        return () => {
            active = false;
        };
    }, [admin.logged, reload, handleUnauthorized]);

    /** Svuota i campi del form e torna alla modalità creazione. */
    const cancelEdit = useCallback(() => {
        setEditing(null);
        setTitle('');
        setLocation('');
        setData(null);
        setImgPath('');
        setImgTouched(false);
    }, []);

    // L'eliminazione chiede conferma: il click sull'icona apre il dialog, la DELETE
    // parte solo dopo il "Elimina".
    const confirmDelete = async () => {
        if (!admin.logged || !pendingDelete) return;
        const event = pendingDelete;
        setError(null);
        setSuccess(null);
        try {
            await deleteEvent(event.event_id);
            setSuccess(`"${event.event_title}" eliminato.`);
            // Se si stava modificando proprio questo evento, il form torna a "Nuovo evento".
            if (editing?.event_id === event.event_id) cancelEdit();
            // Il ricaricamento riconcilia la lista: se l'API risponde 404 (ultimo
            // evento eliminato) il ramo `catch` la svuota, non resta popolata.
            setReload((r) => !r);
        } catch (err: unknown) {
            const status = (err as { response?: { status?: number } }).response?.status;
            if (status === 401) handleUnauthorized();
            // Un errore (es. 409 "evento con richieste collegate") deve essere visibile:
            // prima veniva ignorato e l'eliminazione sembrava riuscita.
            else setError(errorMessage(err, 'Eliminazione non riuscita. Riprova.'));
        } finally {
            setPendingDelete(null);
        }
    };

    /** Porta l'evento nel form e lo precompila per la modifica. */
    const startEdit = (event: EventInfo) => {
        setEditing(event);
        setTitle(event.event_title);
        setLocation(event.event_location ?? '');
        setData(dayjs(event.event_date));
        setImgPath(imageNameOf(event.event_image));
        setImgTouched(false);
        setError(null);
        setSuccess(null);
        // Il form è nella colonna di destra: su schermi stretti va portato in vista.
        formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    /**
     * Crea un nuovo evento oppure aggiorna quello in modifica.
     *
     * In creazione `img_path` è sempre inviato. In modifica lo è solo se l'admin
     * ha toccato il selettore: una chiave `img_path` **assente** lascia la
     * copertina invariata (P4), evitando di sovrascrivere un URL esterno o un
     * percorso `/assets/…` con `/files/<nome>`. Una stringa vuota la rimuove.
     */
    const handleSave = async () => {
        if (!admin.logged || !data) return;
        const savedTitle = title.trim();
        setError(null);
        setSuccess(null);
        try {
            const payload = {
                title: savedTitle,
                location: location.trim(),
                data: data.toDate(),
                // `undefined` è omesso da `JSON.stringify`: in modifica la chiave
                // resta assente se la copertina non è cambiata.
                img_path: !editing || imgTouched ? imagePayloadOf(imgPath) : undefined,
            };

            if (editing) {
                await updateEvent({ id: editing.event_id, ...payload });
                setSuccess(`"${savedTitle}" aggiornato.`);
            } else {
                await addEvent(payload);
                setSuccess(`"${savedTitle}" creato.`);
            }

            cancelEdit();
            setReload((r) => !r);
        } catch (err: unknown) {
            const status = (err as { response?: { status?: number } }).response?.status;
            if (status === 401) handleUnauthorized();
            // 409 (evento già iniziato) e 404 (non trovato) mostrano il messaggio `msg`
            // del backend; il resto ricade sul testo generico. In caso di errore il form
            // resta in modifica, così l'utente non perde i dati inseriti.
            else
                setError(
                    errorMessage(err, editing ? 'Modifica non riuscita. Riprova.' : 'Creazione non riuscita. Riprova.')
                );
        }
    };

    /** Titolo, luogo e data sono obbligatori sia in creazione sia in modifica. */
    const canSave = title.trim().length > 0 && !!data && location.trim().length > 0;

    /** Un evento è iniziato quando la sua data è nel passato (stessa regola della chip). */
    const isStarted = (event: EventInfo) => dayjs(event.event_date).valueOf() <= now;

    return (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <Paper elevation={0} className="border border-white/10 bg-black/40 p-4 lg:col-span-2">
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    I tuoi eventi
                </Typography>
                <Typography variant="body2" color="text.secondary" className="mb-4">
                    Lista dei tuoi prossimi eventi
                </Typography>

                {loading ? (
                    <LinearProgress />
                ) : events.length === 0 ? (
                    <EmptyState message="Non ci sono ancora eventi." />
                ) : (
                    <div className="flex flex-col gap-3">
                        {events.map((event) => (
                            <div key={event.event_id} className="flex items-center gap-4 rounded-2xl bg-white/5 p-3">
                                <img
                                    src={eventImageUrl(event.event_image)}
                                    alt={event.event_title}
                                    className="h-16 w-16 rounded-lg object-cover"
                                />
                                <div className="min-w-0 flex-1">
                                    <Typography variant="body1" sx={{ fontWeight: 700, fontSize: '1.125rem' }} noWrap>
                                        {event.event_title}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1rem' }}>
                                        {formatTime(event.event_date, 'DD/MM/YYYY HH:mm')} — {event.event_location}
                                    </Typography>
                                </div>
                                {!isStarted(event) && (
                                    <IconButton
                                        color="primary"
                                        onClick={() => startEdit(event)}
                                        title="Modifica evento"
                                        aria-label="Modifica evento"
                                    >
                                        <Edit sx={{ fontSize: 24 }} />
                                    </IconButton>
                                )}
                                <IconButton
                                    color="error"
                                    onClick={() => setPendingDelete(event)}
                                    title="Elimina evento"
                                    aria-label="Elimina evento"
                                >
                                    <Close sx={{ fontSize: 24 }} />
                                </IconButton>
                            </div>
                        ))}
                    </div>
                )}
            </Paper>

            <Paper ref={formRef} elevation={0} className="h-fit border border-white/10 bg-black/40 p-4">
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {editing ? 'Modifica evento' : 'Nuovo evento'}
                </Typography>
                <Typography variant="body2" color="text.secondary" className="mb-4">
                    {editing ? "Aggiorna i dati dell'evento" : 'Aggiungi un nuovo evento'}
                </Typography>
                {error && (
                    <Alert severity="error" className="mb-4" onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}
                {success && (
                    <Alert severity="success" className="mb-4" onClose={() => setSuccess(null)}>
                        {success}
                    </Alert>
                )}
                <div className="flex flex-col gap-4">
                    <TextField
                        label="Titolo"
                        required
                        fullWidth
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                    />
                    <TextField
                        label="Posizione / Località"
                        required
                        fullWidth
                        value={location}
                        onChange={(e) => setLocation(e.target.value)}
                    />
                    <DateTimePicker
                        label="Data evento"
                        value={data}
                        onChange={setData}
                        slotProps={{ textField: { fullWidth: true, variant: 'filled' } }}
                    />
                    <FileSelector
                        label="Immagine"
                        value={imgPath}
                        onChange={(name) => {
                            setImgPath(name);
                            setImgTouched(true);
                        }}
                    />
                    {editing ? (
                        <div className="flex gap-2">
                            <Button variant="outlined" fullWidth onClick={cancelEdit}>
                                Annulla
                            </Button>
                            <Button variant="contained" fullWidth disabled={!canSave} onClick={handleSave}>
                                Salva
                            </Button>
                        </div>
                    ) : (
                        <Button variant="contained" fullWidth disabled={!canSave} onClick={handleSave}>
                            Conferma
                        </Button>
                    )}
                </div>
            </Paper>

            <Dialog open={pendingDelete !== null} onClose={() => setPendingDelete(null)}>
                <DialogTitle>Eliminare l&apos;evento?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {pendingDelete ? `"${pendingDelete.event_title}" verrà eliminato definitivamente.` : ''}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPendingDelete(null)}>Annulla</Button>
                    <Button color="error" variant="contained" onClick={confirmDelete}>
                        Elimina
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}
